import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useApi from '../../Api';
import { auth } from '../firebase_utils/firebase';
import { checkActionCode, applyActionCode } from 'firebase/auth';
import { debounce } from 'lodash';
import { Spinner, Button, Form, Alert } from 'react-bootstrap';
import { validateTextInput } from '../common/TextValidator';
import Popup from '../common/popup/Popup';
import { useAuth } from '../../AuthContext';
import '../../styles/components/user/VerifyEmail.css';

const VerifyEmail = ({ show = true, handleClose, switchToLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const apiClient = useApi();
  const { refreshToken } = useAuth();
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl') || '/';
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [resendVisible, setResendVisible] = useState(false);
  const [verificationProcessed, setVerificationProcessed] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const hasRun = useRef(false);

  const verifyEmail = useCallback(async () => {
    if (verificationProcessed || hasRun.current) return;
    hasRun.current = true;
    if (!oobCode) {
      setMessage('No verification code found. Please request a new verification email.');
      setResendVisible(true);
      setLoading(false);
      return;
    }
    try {
      const actionResult = await checkActionCode(auth, oobCode);
      const { email: verifiedEmail } = actionResult.data;
      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      await apiClient.post('/update-email-verified', { email: verifiedEmail });
      await refreshToken();
      setMessage('Your email has been verified. Redirecting to the home page...');
      setRedirectCountdown(5);
    } catch (error) {
      if (error.code === 'auth/expired-action-code') {
        setMessage('This verification link has expired.');
      } else if (error.code === 'auth/invalid-action-code') {
        setMessage('This verification link is invalid or has already been used.');
      } else {
        setMessage('Email verification failed. Please try again.');
      }
      setResendVisible(true);
    } finally {
      setLoading(false);
      setVerificationProcessed(true);
    }
  }, [oobCode, verificationProcessed, apiClient, refreshToken]);

  useEffect(() => {
    if (!verificationProcessed) {
      verifyEmail();
    }
  }, [verifyEmail, verificationProcessed]);

  useEffect(() => {
    if (redirectCountdown > 0) {
      const timer = setTimeout(() => setRedirectCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (redirectCountdown === 0) {
      navigate(continueUrl);
    }
  }, [redirectCountdown, navigate, continueUrl]);

  const debouncedResendVerificationEmail = useMemo(
    () =>
      debounce(async (emailInput, setMsg, setLoad) => {
        if (!emailInput.trim()) {
          setMsg('');
          return;
        }
        const emailValidation = validateTextInput(emailInput, {
          maxLength: 255,
          allowSpecialChars: true,
        });
        if (!emailValidation.isValid) {
          setMsg(emailValidation.errors.join(' '));
          return;
        }
        try {
          setLoad(true);
          await apiClient.post('/resend-verification-email', { email: emailInput });
          setMsg('If the email exists, a verification email has been sent.');
          setResendVisible(false);
        } catch (error) {
          setMsg('Failed to process your request.');
        } finally {
          setLoad(false);
        }
      }, 1000),
    [apiClient]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    debouncedResendVerificationEmail(email, setMessage, setLoading);
  };

  const handleCancel = () => {
    if (handleClose) {
      handleClose();
    } else {
      navigate('/');
    }
  };

  if (!show) return null;

  return (
    <>
      <Helmet>
        <title>Email Verification - WeiFinders</title>
        <meta name="robots" content="noindex" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://www.weifinders.com/verify-email" />
      </Helmet>
      <Popup title="Email Verification" onCancel={handleCancel} className="popup-modal verify-email-modal">
        {loading ? (
          <div className="spinner-container my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Verifying...</span>
            </Spinner>
            <p className="mt-2">Verifying your email, please wait...</p>
          </div>
        ) : (
          <>
            {message && (
              <Alert variant={(message.includes('verified') || message === 'If the email exists, a verification email has been sent.') ? 'success' : 'danger'} className="my-3">
                {message}
              </Alert>
            )}
            {redirectCountdown > 0 && (
              <p className="redirect-countdown">Redirecting in {redirectCountdown} seconds...</p>
            )}
            {resendVisible && (
              <div className="resend-section mt-4">
                <h3>Resend Verification Email</h3>
                <Form className="mt-3" onSubmit={handleSubmit}>
                  <Form.Group>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setMessage('');
                      }}
                      className="form-input"
                      aria-label="Enter your email"
                    />
                  </Form.Group>
                  <Button variant="primary" className="mt-3" type="submit" disabled={loading}>
                    Resend Verification Email
                  </Button>
                </Form>
              </div>
            )}
            {typeof switchToLogin === 'function' && (
              <p className="w-100 text-center mt-3">
                <span onClick={switchToLogin} className="login-link" style={{ cursor: 'pointer' }}>
                  Back to Sign In
                </span>
              </p>
            )}
          </>
        )}
      </Popup>
    </>
  );
};

export default VerifyEmail;
